import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./recommendationCarousel.html";
import { responsiveBinding } from '~/components/wds/common/responsiveHelper';
import { i18nStore } from '~/modules/stores/i18nStore';
const SCROLL_SETTLE_MS = 600;

/**
 * Shared full-bleed recommendation carousel used at the bottom of Listing Detail V2.
 *
 * Callers own only their data mapping and card renderer. This primitive owns the section heading,
 * responsive 3/2/1-card layout, scroll-snap track, paging controls, and all carousel geometry so
 * "More from Provider" and "Related Skills" cannot drift as the UI evolves.
 */
class RecommendationCarousel extends LightningElement {
  constructor(...args) {
    super(...args);
    this.title = '';
    this.previousLabel = '';
    this.nextLabel = '';
    this.pageIndex = 0;
    this.cardsPerPage = 3;
    this._itemCount = 0;
    this.isSmallDevice = false;
    this.isMobile = false;
    this.programmaticScroll = false;
    this.scrollSettleTimer = null;
    this.responsiveDisposers = [];
  }
  get itemCount() {
    return this._itemCount;
  }
  set itemCount(value) {
    const nextItemCount = Math.max(0, Number(value) || 0);
    if (nextItemCount === this._itemCount) {
      return;
    }
    this._itemCount = nextItemCount;
    this.pageIndex = 0;
  }
  connectedCallback() {
    this.responsiveDisposers = [responsiveBinding(RecommendationCarousel.TABLET_MAX_WIDTH, matches => {
      this.isSmallDevice = matches;
      this.updateCardsPerPage();
    }), responsiveBinding(RecommendationCarousel.MOBILE_MAX_WIDTH, matches => {
      this.isMobile = matches;
      this.updateCardsPerPage();
    })];
  }
  disconnectedCallback() {
    this.responsiveDisposers.forEach(dispose => dispose());
    this.responsiveDisposers = [];
    if (this.scrollSettleTimer) {
      clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = null;
    }
  }
  get hasItems() {
    return this.itemCount > 0;
  }
  get pageCount() {
    return Math.max(1, Math.ceil(this.itemCount / this.cardsPerPage));
  }
  get showPagination() {
    return this.pageCount > 1;
  }
  get carouselListClass() {
    return this.showPagination ? 'carousel-list carousel-list--paginated' : 'carousel-list';
  }
  get backArrowDisabled() {
    return this.pageIndex <= 0;
  }
  get forwardArrowDisabled() {
    return this.pageIndex >= this.pageCount - 1;
  }
  get effectivePreviousLabel() {
    return this.previousLabel || i18nStore.getState().i18nStaticCache.moreFromProvider.previousListings;
  }
  get effectiveNextLabel() {
    return this.nextLabel || i18nStore.getState().i18nStaticCache.moreFromProvider.nextListings;
  }
  handlePageBack() {
    if (this.backArrowDisabled) {
      return;
    }
    this.pageIndex -= 1;
    this.scrollToPage(this.pageIndex);
  }
  handlePageForward() {
    if (this.forwardArrowDisabled) {
      return;
    }
    this.pageIndex += 1;
    this.scrollToPage(this.pageIndex);
  }
  handleTrackScroll() {
    this.dispatchEvent(new CustomEvent('trackscroll'));
    if (this.programmaticScroll) {
      return;
    }
    const track = this.trackElement;
    if (!track) {
      return;
    }
    const pageStride = this.pageStride || track.clientWidth || 1;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atEnd = maxScroll > 0 && track.scrollLeft >= maxScroll - 1;
    const derivedPage = atEnd ? this.pageCount - 1 : Math.round(track.scrollLeft / pageStride);
    this.pageIndex = Math.min(Math.max(0, derivedPage), this.pageCount - 1);
  }
  updateCardsPerPage() {
    const nextCardsPerPage = this.isMobile ? 1 : this.isSmallDevice ? 2 : 3;
    if (nextCardsPerPage === this.cardsPerPage) {
      return;
    }
    this.cardsPerPage = nextCardsPerPage;
    if (this.pageIndex > this.pageCount - 1) {
      this.pageIndex = Math.max(0, this.pageCount - 1);
      this.scrollToPage(this.pageIndex);
    }
  }
  scrollToPage(index) {
    const track = this.trackElement;
    if (!track || typeof track.scrollTo !== 'function') {
      return;
    }
    const cards = this.cardElements;
    const firstCard = cards[0];
    const targetCard = cards[index * this.cardsPerPage];
    if (!firstCard || !targetCard) {
      return;
    }
    this.programmaticScroll = true;
    if (this.scrollSettleTimer) {
      clearTimeout(this.scrollSettleTimer);
    }
    this.scrollSettleTimer = setTimeout(() => {
      this.programmaticScroll = false;
      this.scrollSettleTimer = null;
    }, SCROLL_SETTLE_MS);
    track.scrollTo({
      left: targetCard.offsetLeft - firstCard.offsetLeft,
      behavior: 'smooth'
    });
  }
  get pageStride() {
    const cards = this.cardElements;
    if (cards.length < 2) {
      return 0;
    }
    return (cards[1].offsetLeft - cards[0].offsetLeft) * this.cardsPerPage;
  }
  get cardElements() {
    const slot = this.template?.querySelector('slot[name="cards"]');
    return (slot?.assignedElements({
      flatten: true
    }) ?? []).filter(element => element instanceof HTMLElement);
  }
  get trackElement() {
    return this.template?.querySelector('.carousel-track') ?? null;
  }
  /*LWC compiler v7.1.3*/
}
RecommendationCarousel.MOBILE_MAX_WIDTH = '767px';
RecommendationCarousel.TABLET_MAX_WIDTH = '1023px';
_registerDecorators(RecommendationCarousel, {
  publicProps: {
    title: {
      config: 0
    },
    previousLabel: {
      config: 0
    },
    nextLabel: {
      config: 0
    },
    itemCount: {
      config: 3
    }
  },
  fields: ["pageIndex", "cardsPerPage", "_itemCount", "isSmallDevice", "isMobile", "programmaticScroll", "scrollSettleTimer", "responsiveDisposers"]
});
const __lwc_component_class_internal = _registerComponent(RecommendationCarousel, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;